// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useExerciseSubmission } from '@hooks/mutations/ledger/useSubmission';
import { useCreateBurnRequestContext } from '@hooks/mutations/registry/burn/context/useCreateBurnRequestContext';
import { ChoiceContext } from '@daml.js/splice-api-token-metadata/lib/Splice/Api/Token/MetadataV1';
import { Burn } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Model/Burn';
import {
  AllocationFactory,
  AllocationFactory_RequestBurn,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/AllocationFactory';
import { Holding } from '@daml.js/utility-registry-holding-v0/lib/Utility/Registry/Holding/V0/Holding';
import { ContractId } from '@daml/types';
import { toDamlDisclosedContract } from '../../../../utils/common';
import { ERR_REQ_BURN, SUC_REQ_BURN } from '../../../../utils/strings';

interface Args {
  party: string;
  amount: string;
  source: string;
  instrumentId: string;
  holdingCids?: string[];
  reference?: string;
}

export const useRequestBurn2Step = () => {
  const getCreateRequestBurnContext = useCreateBurnRequestContext();
  const { submit: createBurnRequest } = useExerciseSubmission(
    AllocationFactory.AllocationFactory_RequestBurn,
    SUC_REQ_BURN,
    ERR_REQ_BURN,
  );

  const now = new Date();
  return async (args: Args) => {
    const { source, party, amount, instrumentId, holdingCids, reference } = args;

    const createRequestBurnContext = await getCreateRequestBurnContext.mutateAsync({
      registrar: source,
      holder: party,
      holdingContractIds: holdingCids?.length
        ? holdingCids.map((cid) => cid as ContractId<Holding>)
        : [],
      instrumentId,
    });

    const burn: Burn = {
      instrumentId: {
        admin: source,
        id: instrumentId,
      },
      amount,
      holder: party,
      reference: reference || '',
      requestedAt: now.toISOString(),
      executeBefore: new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days later
      meta: { values: {} },
    };

    const payload: AllocationFactory_RequestBurn = {
      expectedAdmin: source,
      burn,
      holdingCids: holdingCids ? holdingCids.map((cid) => cid as ContractId<Holding>) : [],
      extraArgs: {
        context: createRequestBurnContext.choiceContext.choiceContextData as ChoiceContext,
        meta: { values: {} },
      },
    };

    const disclosedContracts =
      createRequestBurnContext.choiceContext.disclosedContracts.map(toDamlDisclosedContract) || [];

    await createBurnRequest({
      cid: createRequestBurnContext.factoryId as ContractId<AllocationFactory>,
      argument: payload,
      meta: {
        disclosedContracts,
      },
    });
  };
};
